import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { publicApiClient } from "../../../api/apiClient";
import "./Partnership.css";
import PartnershipDetail from "./PartnershipDetail";

const FILE_BASE_URL = "https://api.dswustudent.kr";

const TAB = {
  ALL: "ALL",
  FOOD: "FOOD",
  CULTURE: "CULTURE",
  LIFE: "LIFE",
};

const CATEGORY_LABEL = {
  FOOD: "식음료",
  CULTURE: "문화•여가",
  LIFE: "생활 편의",
};

const DURATION_LABEL = {
  LONG: "장기",
  SHORT: "단기",
};

const ITEMS_PER_PAGE = 12;

const currentYear = new Date().getFullYear();
const recentYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

const toAbsoluteUrl = (url) => {
  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  if (url.startsWith("/")) {
    return `${FILE_BASE_URL}${url}`;
  }

  return `${FILE_BASE_URL}/${url}`;
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const formatPeriod = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (!start && !end) return "-";
  if (start && !end) return start;
  if (!start && end) return end;
  if (start === end) return start;

  return `${start}~${end}`;
};

const getYearFromDate = (value) => {
  if (!value) return currentYear;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    const year = Number(String(value).slice(0, 4));
    return Number.isNaN(year) ? currentYear : year;
  }

  return date.getFullYear();
};

const getTimeValue = (value) => {
  if (!value) return 0;

  const date = new Date(value);
  const time = date.getTime();

  return Number.isNaN(time) ? 0 : time;
};

const getListFromResponse = (result) => {
  const data = result?.data || result;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const isEnded = (endDate) => {
  if (!endDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (Number.isNaN(end.getTime())) return false;

  return end < today;
};

const getMainImage = (item) => {
  const mainImg = toAbsoluteUrl(item.mainImg);

  if (mainImg) return mainImg;

  return "https://placehold.co/300x380/F8EFF2/B05B75?text=Partnership";
};

const normalizePartner = (item) => {
  const id = item.partnershipId || item.id;
  const category = item.category || "LIFE";
  const durationType = item.durationType || "LONG";

  return {
    id,
    year: getYearFromDate(item.startDate || item.createdAt),
    category,
    categoryLabel: CATEGORY_LABEL[category] || category,
    durationType,
    durationLabel: DURATION_LABEL[durationType] || durationType,
    name: item.companyName || item.title || "",
    title: item.title || "",
    period: formatPeriod(item.startDate, item.endDate),
    benefit: item.benefitSummary || "",
    content:
      item.content ||
      item.contents ||
      item.description ||
      item.body ||
      item.benefitSummary ||
      "",
    image: getMainImage(item),
    mainImg: item.mainImg || "",
    startDate: item.startDate || "",
    endDate: item.endDate || "",
    createdAtRaw: item.createdAt || "",
    ended: isEnded(item.endDate),
  };
};

const getPartnerDetailContent = async (id) => {
  if (!id) return "";

  try {
    const result = await publicApiClient(`/api/activity/partnership/${id}`, {
      method: "GET",
    });

    const data = result?.data || result;

    return (
      data?.content ||
      data?.contents ||
      data?.description ||
      data?.body ||
      data?.benefitSummary ||
      ""
    );
  } catch (error) {
    console.error("제휴 상세 내용 조회 실패:", error);
    return "";
  }
};

export default function Partnership() {
  const [activeTab, setActiveTab] = useState(TAB.ALL);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [searchType, setSearchType] = useState("제목");
  const [searchTypeOpen, setSearchTypeOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [partners, setPartners] = useState([]);

  const navigate = useNavigate();
  const { id } = useParams();

  const fetchPartners = async () => {
    try {
      setLoading(true);

      const result = await publicApiClient(
        "/api/activity/partnership?listCategory=ALL&page=0&size=100&sort=createdAt,desc",
        {
          method: "GET",
        }
      );

      console.log("제휴 목록 조회 성공:", result);

      const list = getListFromResponse(result)
        .map(normalizePartner)
        .filter((item) => item.id);

      setPartners(list);
    } catch (error) {
      console.error("제휴 목록 조회 실패:", error);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMissingContents = async () => {
    const hasMissingContent = partners.some(
      (partner) => partner.id && !partner.content
    );

    if (!hasMissingContent) return;

    try {
      setContentLoading(true);

      const nextPartners = await Promise.all(
        partners.map(async (partner) => {
          if (!partner.id || partner.content) return partner;

          const detailContent = await getPartnerDetailContent(partner.id);

          return {
            ...partner,
            content: detailContent,
          };
        })
      );

      setPartners(nextPartners);
    } finally {
      setContentLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [selectedYear, searchQuery, activeTab]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchType === "내용") {
        await loadMissingContents();
      }

      setSearchQuery(search);
      setVisibleCount(ITEMS_PER_PAGE);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchType]);

  const currentList = useMemo(() => {
    const yearFiltered =
      selectedYear === "이전"
        ? partners.filter((p) => p.year < currentYear - 4)
        : partners.filter((p) => p.year === selectedYear);

    const tabFiltered =
      activeTab === TAB.ALL
        ? yearFiltered
        : yearFiltered.filter((p) => p.category === activeTab);

    const keyword = searchQuery.trim();

    const searched = tabFiltered.filter((p) => {
      if (!keyword) return true;

      if (searchType === "제목") {
        return (
          p.name.includes(keyword) ||
          p.title.includes(keyword) ||
          p.categoryLabel.includes(keyword)
        );
      }

      if (searchType === "내용") {
        return (
          p.benefit.includes(keyword) ||
          p.content.includes(keyword) ||
          p.period.includes(keyword)
        );
      }

      return true;
    });

    const sortedByCreatedAt = [...searched].sort((a, b) => {
      const timeA = getTimeValue(a.createdAtRaw);
      const timeB = getTimeValue(b.createdAtRaw);

      if (timeA !== timeB) {
        return timeB - timeA;
      }

      return Number(b.id || 0) - Number(a.id || 0);
    });

    return [
      ...sortedByCreatedAt.filter((p) => !p.ended),
      ...sortedByCreatedAt.filter((p) => p.ended),
    ];
  }, [partners, selectedYear, activeTab, searchQuery, searchType]);

  const visible = currentList.slice(0, visibleCount);
  const total = currentList.length;
  const hasMore = visibleCount < total;

  if (id) {
    return <PartnershipDetail />;
  }

  return (
    <>
      <div className="stdlog-partnership-header">
        <h2 className="stdlog-partnership-title">제휴•협력</h2>

        <div className="stdlog-partnership-tabs">
          <button
            className={activeTab === TAB.ALL ? "active" : ""}
            onClick={() => setActiveTab(TAB.ALL)}
            type="button"
          >
            전체
          </button>

          <button
            className={activeTab === TAB.FOOD ? "active" : ""}
            onClick={() => setActiveTab(TAB.FOOD)}
            type="button"
          >
            식음료
          </button>

          <button
            className={activeTab === TAB.CULTURE ? "active" : ""}
            onClick={() => setActiveTab(TAB.CULTURE)}
            type="button"
          >
            문화•여가
          </button>

          <button
            className={activeTab === TAB.LIFE ? "active" : ""}
            onClick={() => setActiveTab(TAB.LIFE)}
            type="button"
          >
            생활 편의
          </button>
        </div>
      </div>

      <div className="partnership-page">
        <div className="partnership-filterbar">
          <div className="partnership-year-dropdown">
            <button
              className="partnership-year-btn"
              onClick={() => setYearDropdownOpen((v) => !v)}
              type="button"
            >
              {selectedYear === "이전" ? "2021년 이전" : `${selectedYear}년`}

              <svg
                width="14"
                height="8"
                viewBox="0 0 24 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  transform: yearDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                <polyline points="2 2 12 10 22 2" />
              </svg>
            </button>

            {yearDropdownOpen && (
              <ul className="partnership-year-list">
                {recentYears.map((y) => (
                  <li
                    key={y}
                    className={`partnership-year-item ${
                      selectedYear === y ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedYear(y);
                      setYearDropdownOpen(false);
                    }}
                  >
                    {y}년
                  </li>
                ))}

                <li
                  className={`partnership-year-item ${
                    selectedYear === "이전" ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedYear("이전");
                    setYearDropdownOpen(false);
                  }}
                >
                  2021년 이전
                </li>
              </ul>
            )}
          </div>

          <div className="partnership-search-area">
            <div className="partnership-search-type">
              <button
                className="partnership-search-type-btn"
                onClick={() => setSearchTypeOpen((v) => !v)}
                type="button"
              >
                {searchType}

                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 24 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: searchTypeOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                >
                  <polyline points="2 2 12 10 22 2" />
                </svg>
              </button>

              {searchTypeOpen && (
                <ul className="partnership-search-type-list">
                  {["제목", "내용"].map((t) => (
                    <li
                      key={t}
                      className={`partnership-search-type-item ${
                        searchType === t ? "active" : ""
                      }`}
                      onClick={() => {
                        setSearchType(t);
                        setSearchTypeOpen(false);
                      }}
                    >
                      {t}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="partnership-search-wrap">
              <input
                className="partnership-search-input"
                type="text"
                placeholder="검색어를 입력하세요."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <button
                className="partnership-search-btn"
                type="button"
                onClick={() => setVisibleCount(ITEMS_PER_PAGE)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="partnership-grid">
          {loading || contentLoading ? (
            Array(12)
              .fill(0)
              .map((_, i) => (
                <div className="partnership-skeleton-card" key={i}>
                  <div className="partnership-skeleton-img" />
                  <div className="partnership-skeleton-body">
                    <div className="partnership-skeleton-line short" />
                    <div className="partnership-skeleton-line" />
                    <div className="partnership-skeleton-line medium" />
                  </div>
                </div>
              ))
          ) : (
            <>
              {visible.length === 0 && (
                <p className="partnership-empty">작성된 글이 없습니다.</p>
              )}

              {visible.map((partner) => (
                <div
                  className={`partnership-card ${
                    partner.ended ? "ended" : ""
                  }`}
                  key={partner.id}
                  onClick={() =>
                    navigate(`/stdcouncil-log/Partnership/${partner.id}`)
                  }
                >
                  <div className="partnership-card-img-wrap">
                    {partner.ended && (
                      <span className="partnership-ended-tag">제휴 종료</span>
                    )}

                    <img
                      src={partner.image}
                      alt={partner.name}
                      className="partnership-card-img"
                    />
                  </div>

                  <div className="partnership-card-body">
                    <p className="partnership-card-period">
                      [{partner.durationLabel}] {partner.period}
                    </p>
                    <h3 className="partnership-card-name">{partner.name}</h3>
                    <p className="partnership-card-benefit">
                      {partner.benefit}
                    </p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {!loading && !contentLoading && hasMore && (
          <div className="partnership-more">
            <button
              onClick={() => setVisibleCount((v) => v + ITEMS_PER_PAGE)}
              type="button"
            >
              더보기 ({Math.min(visibleCount, total)}/{total})

              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <polyline points="3 8 12 16 21 8" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
}